import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import {
  CheckCircle2, Clock, AlertTriangle, Users,
  ArrowLeftRight, X, ChevronDown, ExternalLink, MessageSquare,
  CalendarDays,
} from "lucide-react";
import { useEmployeeStats, useEmployeeSubTasks } from "@/features/tasks/hooks/useSubTasks";
import { SubTaskDetailPanel } from "@/features/tasks/components/SubTaskDetailPanel";
import {
  TASK_STATUS_COLORS, TASK_STATUS_LABELS, PRIORITY_DOT,
  type EmployeeTaskStats, type SubTask,
} from "@/features/tasks/types";
import { Spinner } from "@/components/ui/Spinner";
import { getRoleLabel } from "@/utils/roleLabel";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { formatDate } from "@/utils/formatDate";
import { BackToFridayLink } from "@/features/tasks/components/BackToFridayLink";

const STATUS_ORDER = ["todo_now", "waiting", "todo_later"];

// ── Small shared components ───────────────────────────────────────────────────

function Avatar({ url, name, userId, size = "md" }: { url: string | null; name: string; userId?: string; size?: "sm" | "md" | "lg" | "xl" }) {
  const avatarSize = { sm: "sm", md: "md", lg: "lg", xl: "xl" }[size] as "sm" | "md" | "lg" | "xl";
  return (
    <UserAvatar
      userId={userId}
      fullName={name}
      profilePictureUrl={url}
      size={avatarSize}
      initialsClassName="bg-[var(--color-primary)] text-white font-semibold"
    />
  );
}

function ProgressBar({ done, total, className }: { done: number; total: number; className?: string }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className={clsx("space-y-1", className)}>
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>Ukończone {pct}%</span>
        <span>{done} / {total}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
        <div className="h-full rounded-full bg-[var(--bg-elevated)]0 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Employee card (grid) ──────────────────────────────────────────────────────

function EmployeeCard({ data, selected, onClick }: { data: EmployeeTaskStats; selected: boolean; onClick: () => void }) {
  const total = data.done + data.total_active;
  return (
    <div
      onClick={onClick}
      className={clsx(
        "bg-white dark:bg-gray-800 rounded-xl border p-4 cursor-pointer transition-all",
        selected
          ? "border-[var(--color-primary)] ring-2 ring-[var(--color-primary-glow)] dark:ring-blue-800 shadow-md"
          : "border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600",
        !selected && data.overdue > 0 && "border-l-4 border-l-red-400",
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <Avatar url={data.profile_picture_url} name={data.full_name} userId={data.user_id} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{data.full_name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleLabel(data.role, data.is_ceo)}</p>
        </div>
        {data.overdue > 0 && (
          <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded-full flex-shrink-0">
            <AlertTriangle size={10} />
            {data.overdue}
          </span>
        )}
      </div>

      <ProgressBar done={data.done} total={total} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 text-center">
        {[
          { v: data.done, label: "Zrobione", c: "text-green-600 dark:text-green-400" },
          { v: data.todo_now, label: "W toku", c: "text-[var(--color-primary)] dark:text-blue-400" },
          { v: data.waiting, label: "Wstrzymano", c: "text-yellow-600 dark:text-yellow-400" },
          { v: data.todo_later, label: "Później", c: "text-gray-500 dark:text-gray-400" },
        ].map(({ v, label, c }) => (
          <div key={label}>
            <p className={clsx("text-base font-bold", c)}>{v}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Subtask row (inside drawer) ───────────────────────────────────────────────

function DrawerSubTaskRow({ st, onClick }: { st: SubTask; onClick: () => void }) {
  const navigate = useNavigate();

  const dueLabel = st.due_date ? formatDate(st.due_date) : null;
  const isOverdue =
    st.due_date &&
    new Date(st.due_date) < new Date() &&
    !["done", "cancelled", "n_d"].includes(st.status);

  return (
    <div
      onClick={onClick}
      className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/20 cursor-pointer group transition-colors border border-transparent hover:border-[var(--color-primary)] dark:hover:border-[var(--color-primary)]"
    >
      <div className={clsx("w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0", PRIORITY_DOT[st.priority])} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate leading-snug">{st.title}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
          {st.task_title}{st.task_project_name ? ` · ${st.task_project_name}` : ""}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className={clsx("text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap", TASK_STATUS_COLORS[st.status])}>
            {TASK_STATUS_LABELS[st.status]}
          </span>
          {dueLabel && (
            <span className={clsx("flex items-center gap-0.5 text-xs", isOverdue ? "text-red-500 font-medium" : "text-gray-400 dark:text-gray-500")}>
              <CalendarDays size={11} />
              {dueLabel}
            </span>
          )}
          {st.comments_count > 0 && (
            <span className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
              <MessageSquare size={11} />
              {st.comments_count}
            </span>
          )}
        </div>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/tasks/board?task=${st.task}&subtask=${st.id}`);
        }}
        className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded text-gray-400 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] transition-all"
        title="Otwórz w tablicy"
      >
        <ExternalLink size={14} />
      </button>
    </div>
  );
}

// ── Employee detail drawer ────────────────────────────────────────────────────

function EmployeeDetailDrawer({ employee, onClose }: { employee: EmployeeTaskStats; onClose: () => void }) {
  const { data: subtasks = [], isLoading } = useEmployeeSubTasks(employee.user_id);
  const [subtaskDetail, setSubtaskDetail] = useState<{ taskId: string; subtaskId: string } | null>(null);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !subtaskDetail) onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, subtaskDetail]);

  const active = useMemo(
    () =>
      subtasks
        .filter((s) => !["done", "cancelled"].includes(s.status))
        .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)),
    [subtasks],
  );
  const done = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 14);
    return subtasks.filter((s) => s.status === "done" && new Date(s.updated_at) >= cutoff);
  }, [subtasks]);
  const cancelled = useMemo(() => subtasks.filter((s) => s.status === "cancelled"), [subtasks]);
  const total = employee.done + employee.total_active;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-[70] flex w-full max-w-lg flex-col bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <Avatar url={employee.profile_picture_url} name={employee.full_name} userId={employee.user_id} size="xl" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">{employee.full_name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{getRoleLabel(employee.role, employee.is_ceo)}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          {[
            { label: "Zrobione", value: employee.done, bg: "bg-green-50 dark:bg-green-900/20", c: "text-green-700 dark:text-green-400" },
            { label: "W toku", value: employee.todo_now, bg: "bg-blue-50 dark:bg-blue-900/20", c: "text-[var(--color-primary)] dark:text-blue-400" },
            { label: "Wstrzymano", value: employee.waiting, bg: "bg-yellow-50 dark:bg-yellow-900/20", c: "text-yellow-700 dark:text-yellow-400" },
            {
              label: "Zaległe", value: employee.overdue,
              bg: employee.overdue > 0 ? "bg-red-50 dark:bg-red-900/20" : "bg-gray-50 dark:bg-gray-800",
              c: employee.overdue > 0 ? "text-red-600 dark:text-red-400" : "text-gray-400 dark:text-gray-500",
            },
          ].map(({ label, value, bg, c }) => (
            <div key={label} className={clsx("rounded-xl p-3 text-center", bg)}>
              <p className={clsx("text-2xl font-bold", c)}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <ProgressBar done={employee.done} total={total} />
        </div>

        {/* Scrollable subtask list */}
        <div className="flex-1 overflow-y-auto">
          {/* Active subtasks */}
          <div className="px-6 pt-5 pb-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Aktywne zadania
              <span className="ml-1.5 normal-case font-normal text-gray-400">({active.length})</span>
            </p>
            {isLoading ? (
              <div className="flex justify-center py-6"><Spinner size="sm" /></div>
            ) : active.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">Brak aktywnych zadań</p>
            ) : (
              <div className="space-y-1.5">
                {active.map((st) => (
                  <DrawerSubTaskRow
                    key={st.id}
                    st={st}
                    onClick={() => setSubtaskDetail({ taskId: st.task, subtaskId: st.id })}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Done subtasks (collapsible) */}
          {done.length > 0 && (
            <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
              <details className="group">
                <summary className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer select-none list-none">
                  <ChevronDown size={13} className="transition-transform group-open:rotate-180 text-gray-400" />
                  Ukończone
                  <span className="normal-case font-normal text-gray-400">({done.length})</span>
                  <span className="normal-case font-normal text-gray-400 text-xs">· ostatnie 14 dni</span>
                </summary>
                <div className="space-y-1.5 mt-3">
                  {done.map((st) => (
                    <DrawerSubTaskRow
                      key={st.id}
                      st={st}
                      onClick={() => setSubtaskDetail({ taskId: st.task, subtaskId: st.id })}
                    />
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Cancelled subtasks (collapsible) */}
          {cancelled.length > 0 && (
            <div className="px-6 pb-6 border-t border-gray-100 dark:border-gray-700 pt-4">
              <details className="group">
                <summary className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide cursor-pointer select-none list-none">
                  <ChevronDown size={13} className="transition-transform group-open:rotate-180 text-gray-400" />
                  Anulowane
                  <span className="normal-case font-normal text-gray-400">({cancelled.length})</span>
                </summary>
                <div className="space-y-1.5 mt-3">
                  {cancelled.map((st) => (
                    <DrawerSubTaskRow
                      key={st.id}
                      st={st}
                      onClick={() => setSubtaskDetail({ taskId: st.task, subtaskId: st.id })}
                    />
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* Nested full subtask detail (with comments / history) */}
      {subtaskDetail && (
        <SubTaskDetailPanel
          taskId={subtaskDetail.taskId}
          subtaskId={subtaskDetail.subtaskId}
          onClose={() => setSubtaskDetail(null)}
        />
      )}
    </>,
    document.body
  );
}

// ── 1:1 Comparison ────────────────────────────────────────────────────────────

interface CompareMetric {
  label: string;
  aVal: number;
  bVal: number;
  higherIsBetter: boolean;
  aColor: string;
  bColor: string;
}

function CompareRow({ m }: { m: CompareMetric }) {
  const aWins = m.higherIsBetter ? m.aVal > m.bVal : m.aVal < m.bVal;
  const bWins = m.higherIsBetter ? m.bVal > m.aVal : m.bVal < m.aVal;
  const maxVal = Math.max(m.aVal, m.bVal, 1);

  return (
    <div className="grid grid-cols-[1fr_130px_1fr] items-center gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex flex-col items-end gap-1">
        <span className={clsx("text-xl font-bold", aWins ? m.aColor : "text-gray-300 dark:text-gray-600")}>
          {m.aVal}{aWins && <span className="text-xs ml-0.5">★</span>}
        </span>
        <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className={clsx("h-full rounded-full transition-all", aWins ? "bg-[var(--bg-elevated)]0" : "bg-gray-200 dark:bg-gray-600")}
            style={{ width: `${(m.aVal / maxVal) * 100}%` }}
          />
        </div>
      </div>

      <p className="text-xs text-center font-medium text-gray-600 dark:text-gray-400">{m.label}</p>

      <div className="flex flex-col items-start gap-1">
        <span className={clsx("text-xl font-bold", bWins ? m.bColor : "text-gray-300 dark:text-gray-600")}>
          {bWins && <span className="text-xs mr-0.5">★</span>}{m.bVal}
        </span>
        <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className={clsx("h-full rounded-full transition-all", bWins ? "bg-[var(--bg-elevated)]0" : "bg-gray-200 dark:bg-gray-600")}
            style={{ width: `${(m.bVal / maxVal) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function ComparisonModule({ stats }: { stats: EmployeeTaskStats[] }) {
  const [idA, setIdA] = useState("");
  const [idB, setIdB] = useState("");
  const a = stats.find((s) => s.user_id === idA);
  const b = stats.find((s) => s.user_id === idB);

  const metrics: CompareMetric[] = a && b
    ? [
        { label: "Zrobione", aVal: a.done, bVal: b.done, higherIsBetter: true, aColor: "text-green-600 dark:text-green-400", bColor: "text-green-600 dark:text-green-400" },
        { label: "W toku (pilne)", aVal: a.todo_now, bVal: b.todo_now, higherIsBetter: false, aColor: "text-[var(--color-primary)] dark:text-blue-400", bColor: "text-[var(--color-primary)] dark:text-blue-400" },
        { label: "Do zrobienia", aVal: a.todo_later, bVal: b.todo_later, higherIsBetter: false, aColor: "text-gray-600 dark:text-gray-300", bColor: "text-gray-600 dark:text-gray-300" },
        { label: "Wstrzymano", aVal: a.waiting, bVal: b.waiting, higherIsBetter: false, aColor: "text-yellow-600 dark:text-yellow-400", bColor: "text-yellow-600 dark:text-yellow-400" },
        { label: "Zaległe", aVal: a.overdue, bVal: b.overdue, higherIsBetter: false, aColor: "text-red-600 dark:text-red-400", bColor: "text-red-600 dark:text-red-400" },
        { label: "Łącznie aktywnych", aVal: a.total_active, bVal: b.total_active, higherIsBetter: false, aColor: "text-purple-600 dark:text-purple-400", bColor: "text-purple-600 dark:text-purple-400" },
      ]
    : [];

  const selectClass = "block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]";

  return (
    <div className="bg-white dark:bg-gray-800 p-6">
      <div className="grid grid-cols-[1fr_40px_1fr] gap-3 mb-6">
        <select value={idA} onChange={(e) => setIdA(e.target.value)} className={selectClass}>
          <option value="">— Osoba A —</option>
          {stats.filter((s) => s.user_id !== idB).map((s) => (
            <option key={s.user_id} value={s.user_id}>{s.full_name}</option>
          ))}
        </select>
        <div className="flex items-center justify-center">
          <span className="text-xs font-bold text-gray-400 dark:text-gray-500">VS</span>
        </div>
        <select value={idB} onChange={(e) => setIdB(e.target.value)} className={selectClass}>
          <option value="">— Osoba B —</option>
          {stats.filter((s) => s.user_id !== idA).map((s) => (
            <option key={s.user_id} value={s.user_id}>{s.full_name}</option>
          ))}
        </select>
      </div>

      {a && b ? (
        <>
          <div className="grid grid-cols-[1fr_40px_1fr] gap-3 mb-4">
            <div className="flex flex-col items-end gap-1">
              <Avatar url={a.profile_picture_url} name={a.full_name} userId={a.user_id} size="lg" />
              <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 text-right">{a.full_name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleLabel(a.role, a.is_ceo)}</p>
            </div>
            <div />
            <div className="flex flex-col items-start gap-1">
              <Avatar url={b.profile_picture_url} name={b.full_name} userId={b.user_id} size="lg" />
              <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{b.full_name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleLabel(b.role, b.is_ceo)}</p>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
            {metrics.map((m, i) => <CompareRow key={i} m={m} />)}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <ArrowLeftRight size={28} className="text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Wybierz dwie osoby, aby porównać aktywność
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function EmployeeProgressPage() {
  const { data: stats = [], isLoading, isError, error } = useEmployeeStats();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedEmployee = useMemo(() => stats.find((s) => s.user_id === selectedId) ?? null, [stats, selectedId]);

  const totals = useMemo(
    () => ({
      done: stats.reduce((s, e) => s + e.done, 0),
      active: stats.reduce((s, e) => s + e.total_active, 0),
      overdue: stats.reduce((s, e) => s + e.overdue, 0),
    }),
    [stats],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    const msg = (error as { message?: string })?.message ?? "Nieznany błąd";
    return (
      <div className="p-6 max-w-lg">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4">
          <p className="font-semibold text-red-700 dark:text-red-400 mb-1">Błąd ładowania danych</p>
          <p className="text-sm text-red-600 dark:text-red-300">{msg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Header */}
      <div>
        {/* Widok wywołany z sekcji „Postęp prac" w Friday — droga powrotna. */}
        <BackToFridayLink className="mb-2" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Postępy pracowników</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Realizacja podzadań w zespole</p>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Pracownicy", value: stats.length, icon: <Users size={18} />, color: "text-[var(--color-primary)] dark:text-blue-400", bg: "bg-blue-50 dark:bg-blue-900/30" },
          { label: "Zrealizowane", value: totals.done, icon: <CheckCircle2 size={18} />, color: "text-green-600 dark:text-green-400", bg: "bg-green-50 dark:bg-green-900/30" },
          { label: "W realizacji", value: totals.active, icon: <Clock size={18} />, color: "text-[var(--color-primary)] dark:text-indigo-400", bg: "bg-indigo-50 dark:bg-indigo-900/30" },
          { label: "Zaległe", value: totals.overdue, icon: <AlertTriangle size={18} />, color: totals.overdue > 0 ? "text-red-600 dark:text-red-400" : "text-gray-400", bg: totals.overdue > 0 ? "bg-red-50 dark:bg-red-900/30" : "bg-gray-50 dark:bg-gray-700/50" },
        ].map((c) => (
          <div key={c.label} className="bg-white dark:bg-gray-800 p-4 flex items-center gap-3">
            <div className={clsx("p-2 rounded-lg", c.bg)}>
              <span className={c.color}>{c.icon}</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{c.value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{c.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Employee grid */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Wszyscy pracownicy ({stats.length})
          <span className="ml-2 normal-case font-normal text-gray-400 dark:text-gray-500">— kliknij kartę, aby zobaczyć szczegóły</span>
        </h2>

        {stats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users size={32} className="text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Brak danych pracowników</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stats.map((emp) => (
              <EmployeeCard
                key={emp.user_id}
                data={emp}
                selected={emp.user_id === selectedId}
                onClick={() => setSelectedId((prev) => (prev === emp.user_id ? null : emp.user_id))}
              />
            ))}
          </div>
        )}
      </div>

      {/* 1:1 Comparison — temporarily hidden */}
      {false && (
      <div>
        <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <ArrowLeftRight size={13} />
          Porównanie 1:1
        </h2>
        <ComparisonModule stats={stats} />
      </div>
      )}

      {/* Employee detail drawer (overlay) */}
      {selectedEmployee && (
        <EmployeeDetailDrawer
          employee={selectedEmployee}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
